/**
 * HH Finance - Aging Summary Component
 *
 * Displays AR/AP aging buckets
 */

import React from 'react';
import styled from 'styled-components';
import { Card } from '@shared/components/card';
import { formatCurrency } from '../utils';
import type { AgingBucket } from '../types';

export interface AgingSummaryProps {
  title: string;
  icon?: string;
  aging: AgingBucket;
  type: 'receivable' | 'payable';
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
  margin: 0;
`;

const Icon = styled.span`
  font-size: 20px;
`;

const BucketsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
`;

const Bucket = styled.div`
  text-align: center;
  padding: 12px 8px;
  background-color: ${({ theme }) => theme.backgroundSecondary || '#f9fafb'};
  border-radius: 8px;
`;

const BucketLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const BucketValue = styled.div<{ severity: 'normal' | 'warning' | 'danger' }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme, severity }) =>
    severity === 'danger' ? theme.error || '#ef4444' :
    severity === 'warning' ? theme.warning || '#f59e0b' :
    theme.textPrimary || '#1a1a1a'};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.border || '#e5e5e5'};
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary || '#666'};
`;

const TotalValue = styled.span<{ type: 'receivable' | 'payable' }>`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme, type }) =>
    type === 'receivable' ? theme.success || '#22c55e' : theme.warning || '#f59e0b'};
`;

export const AgingSummary: React.FC<AgingSummaryProps> = ({
  title,
  icon,
  aging,
  type,
}) => {
  const buckets = [
    { key: 'current', label: 'Current', severity: 'normal' as const },
    { key: '1-30', label: '1-30 Days', severity: 'normal' as const },
    { key: '31-60', label: '31-60 Days', severity: 'warning' as const },
    { key: '61-90', label: '61-90 Days', severity: 'warning' as const },
    { key: '90+', label: '90+ Days', severity: 'danger' as const },
  ];

  return (
    <Card>
      <Header>
        {icon && <Icon>{icon}</Icon>}
        <Title>{title}</Title>
      </Header>

      <BucketsGrid>
        {buckets.map(({ key, label, severity }) => (
          <Bucket key={key}>
            <BucketLabel>{label}</BucketLabel>
            <BucketValue severity={severity}>
              {formatCurrency(aging[key as keyof AgingBucket] as number)}
            </BucketValue>
          </Bucket>
        ))}
      </BucketsGrid>

      <TotalRow>
        <TotalLabel>Total</TotalLabel>
        <TotalValue type={type}>
          {formatCurrency(aging.total)}
        </TotalValue>
      </TotalRow>
    </Card>
  );
};

export default AgingSummary;
